import React, { useState } from 'react';
import {
  HardDrive, Shield, BarChart3, Database, AlertTriangle, CheckCircle, Clock,
} from 'lucide-react';

const STORAGE_BLUEPRINTS = [
  { id: 'storage-provisioning', name: 'Storage Volume Provisioning', description: 'Create LUN/NFS share, assign to host/cluster, zone SAN fabric, update CMDB.', icon: Database, difficulty: 'intermediate', duration: '~5 min', category: 'Provisioning', rating: 4.7, usageCount: 876 },
  { id: 'storage-backup-replication', name: 'Backup & Replication Automation', description: 'Automate VM backup schedules, configure DR replication, verify jobs, alert on failures.', icon: Shield, difficulty: 'intermediate', duration: '~15 min', category: 'Backup', rating: 4.8, usageCount: 1243 },
  { id: 'storage-capacity-management', name: 'Storage Capacity Management', description: 'Monitor utilization, predict exhaustion, auto-provision capacity at thresholds.', icon: BarChart3, difficulty: 'advanced', duration: '~10 min', category: 'Capacity', rating: 4.7, usageCount: 654 },
];

const STATS = [
  { label: 'Total Capacity', value: '847 TB', icon: HardDrive, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { label: 'Used', value: '61%', icon: BarChart3, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { label: 'Failed Backups', value: '2', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Protected VMs', value: '1,142', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function StoragePage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Provisioning', 'Backup', 'Capacity'];
  const filtered = activeCategory === 'All' ? STORAGE_BLUEPRINTS : STORAGE_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-cyan-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Storage</h1>
          </div>
          <p className="text-gray-500">NetApp · Pure Storage · Veeam · SAN/NFS provisioning and data protection</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-emerald-300 font-medium">3 Arrays Connected</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Array Status */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Array Health</h3>
        <div className="space-y-3">
          {[
            { name: 'NetApp AFF-A400 (Primary)', used: 72, capacity: '240 TB', status: 'healthy', replication: 'Replicating to DR' },
            { name: 'Pure Storage FlashArray //C (Tier-1)', used: 58, capacity: '96 TB', status: 'healthy', replication: 'Sync to Primary' },
            { name: 'Veeam Repository (Backup)', used: 45, capacity: '512 TB', status: 'healthy', replication: 'Offsite: Active' },
          ].map((arr) => (
            <div key={arr.name} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${arr.status === 'healthy' ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white font-medium">{arr.name}</span>
                  <span className="text-xs text-gray-400">{arr.used}% used of {arr.capacity}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full mb-1">
                  <div className={`h-full rounded-full ${arr.used > 80 ? 'bg-red-500' : arr.used > 65 ? 'bg-amber-500' : 'bg-cyan-500'}`} style={{ width: `${arr.used}%` }} />
                </div>
                <span className="text-xs text-gray-400">{arr.replication}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Automation Blueprints</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-cyan-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>{bp.difficulty}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-cyan-300 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{bp.duration}</span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
